import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './AttendanceView.css';
import PrintUtility from './utilities/PrintUtility';
import ExportUtility from './utilities/ExportUtility';

const AttendanceView = () => {
    const [loading, setLoading] = useState(false);
    const [attendanceData, setAttendanceData] = useState([]);
    const [summary, setSummary] = useState({});
    const [dateRange, setDateRange] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [holidays, setHolidays] = useState([]);

    const [filters, setFilters] = useState({
        from_date: new Date().toISOString().split('T')[0],
        to_date: new Date().toISOString().split('T')[0],
        unit_id: '',
        department_id: '',
        view_type: 'list' // list or card
    });

    const [departments, setDepartments] = useState([]);
    const [units, setUnits] = useState([]);
    const [expandedRows, setExpandedRows] = useState({});
    const [selectedDate, setSelectedDate] = useState(null);

    const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';

    // Fetch departments
    const fetchDepartments = useCallback(async () => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.get(`${API_BASE}/get_departments.php`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                setDepartments(response.data.departments || []);
            }
        } catch (error) {
            console.error('Error fetching departments:', error);
        }
    }, [API_BASE]);

    // Fetch units
    const fetchUnits = useCallback(async () => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.get(`${API_BASE}/get_units.php`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                setUnits(response.data.units || []);
            }
        } catch (error) {
            console.error('Error fetching units:', error);
        }
    }, [API_BASE]);

    // Fetch attendance data
    const fetchAttendanceData = async () => {
        if (!filters.from_date || !filters.to_date) {
            alert('Please select both From Date and To Date');
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.post(`${API_BASE}/attendance/get_attendance_view.php`,
                filters,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                setAttendanceData(response.data.data || []);
                setSummary(response.data.summary || {});
                setDateRange(response.data.date_range || []);
                setStatuses(response.data.statuses || []);
                setHolidays(response.data.holidays || []);
                // Reset expanded rows
                setExpandedRows({});
            } else {
                alert(`Error: ${response.data.error || 'Unknown error'}`);
            }
        } catch (error) {
            console.error('Error fetching attendance data:', error);
            alert(`Error: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDepartments();
        fetchUnits();
        // Set default dates (current month)
        const today = new Date();
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 2);
        const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 1);

        setFilters(prev => ({
            ...prev,
            from_date: firstDay.toISOString().split('T')[0],
            to_date: lastDay.toISOString().split('T')[0]
        }));
    }, [fetchDepartments, fetchUnits]);

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const toggleRowExpand = (empId) => {
        setExpandedRows(prev => ({
            ...prev,
            [empId]: !prev[empId]
        }));
    };

    const handleDateClick = (date) => {
        setSelectedDate(selectedDate === date ? null : date);
    };

    const getStatusColor = (statusCode, attValue, isHoliday, isWeeklyOff) => {
        // Check for HP (Holiday Present) or WP (Weekly Off Present)
        if (statusCode === 'HP') return 'purple';
        if (statusCode === 'WP') return 'indigo';

        if (attValue >= 1) return 'success';
        if (attValue === 0) return 'danger';
        if (attValue > 0 && attValue < 1) return 'warning';

        switch (statusCode) {
            case 'P': return 'success';
            case 'A': return 'danger';
            case 'PL': return 'info';
            case 'PA': return 'warning';
            default: return 'secondary';
        }
    };

    const getStatusBadge = (dayData) => {
        if (dayData.is_holiday) {
            if (dayData.status_code === 'HP') {
                return (
                    <span className="badge bg-purple" title={`Holiday Present: ${dayData.holiday_name}`}>
                        HP
                    </span>
                );
            }
            return (
                <span className="badge bg-purple" title={`${dayData.holiday_name} (${dayData.holiday_type})`}>
                    H
                </span>
            );
        }
        if (dayData.is_layoff) {
            return (
                <span className="badge bg-dark" title={`Layoff: ${dayData.layoff_reason}`}>
                    LO
                </span>
            );
        }
        if (dayData.is_weeklyoff) {
            if (dayData.status_code === 'WP') {
                return (
                    <span className="badge bg-indigo" title="Weekly Off Present">
                        WP
                    </span>
                );
            }
            return (
                <span className="badge bg-secondary">
                    WO
                </span>
            );
        }
        if (dayData.status_code) {
            const color = getStatusColor(dayData.status_code, dayData.att_value, dayData.is_holiday, dayData.is_weeklyoff);
            return (
                <span className={`badge bg-${color}`} title={dayData.status_name}>
                    {dayData.status_code}
                </span>
            );
        }
        return <span className="badge bg-light text-dark">-</span>;
    };

    const exportToExcel = async () => {
        try {
            const config = {
                attendanceData,
                filters,
                summary,
                dateRange,
                holidays,
                statuses
            };
            const fileName = await ExportUtility.exportToExcel(config);
            console.log(`Exported to: ${fileName}`);
        } catch (error) {
            console.error('Export failed:', error);
        }
    };

    const exportToPDF = async () => {
        try {
            const config = {
                attendanceData,
                filters,
                summary,
                dateRange,
                holidays
            };
            const fileName = await ExportUtility.exportToPDF(config);
            console.log(`Exported to: ${fileName}`);
        } catch (error) {
            console.error('Export failed:', error);
        }
    };

    const printReport = () => {
        const config = {
            attendanceData,
            filters,
            summary,
            dateRange,
            holidays
        };
        PrintUtility.printAttendanceReport(config);
    };

    // Render List View
    const renderListView = () => {
        return (
            <div className="table-responsive attendance-table">
                <table className="table table-bordered table-hover table-sm">
                    <thead className="table-light sticky-header">
                        <tr>
                            <th rowSpan="2" className="text-center align-middle">S.No</th>
                            <th rowSpan="2" className="text-center align-middle">Unit</th>
                            <th rowSpan="2" className="text-center align-middle">Employee Name</th>
                            <th rowSpan="2" className="text-center align-middle">Department</th>
                            <th rowSpan="2" className="text-center align-middle">Designation</th>
                            <th rowSpan="2" className="text-center align-middle">ED</th>
                            <th rowSpan="2" className="text-center align-middle">WD</th>
                            <th rowSpan="2" className="text-center align-middle">FH</th>
                            <th rowSpan="2" className="text-center align-middle">LOff</th>
                            <th rowSpan="2" className="text-center align-middle">PL</th>
                            <th rowSpan="2" className="text-center align-middle">LOP</th>
                            {dateRange.map(date => (
                                <th key={date} className="text-center">
                                    <div className="d-flex flex-column">
                                        <small>{new Date(date).getDate()}</small>
                                        <small className="text-muted">
                                            {new Date(date).toLocaleDateString('en-US', { weekday: 'short' })}
                                        </small>
                                    </div>
                                </th>
                            ))}
                        </tr>
                        <tr>
                            {dateRange.map(date => {
                                const holiday = holidays.find(h => h.hdate === date);
                                return (
                                    <th key={`status-${date}`} className="text-center p-1">
                                        {holiday ? (
                                            <span className="badge bg-purple" title={holiday.hname}>
                                                H
                                            </span>
                                        ) : (
                                            <small className="text-muted">Status</small>
                                        )}
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {attendanceData.map((emp, index) => (
                            <React.Fragment key={emp.emp_id}>
                                <tr className={expandedRows[emp.emp_id] ? 'table-active' : ''}>
                                    <td className="text-center">{emp.sno}</td>
                                    <td>{emp.unit_name}</td>
                                    <td>
                                        <div className="d-flex align-items-center">
                                            <button
                                                className="btn btn-sm btn-link p-0 me-1"
                                                onClick={() => toggleRowExpand(emp.emp_id)}
                                                title={expandedRows[emp.emp_id] ? 'Collapse' : 'Expand'}
                                            >
                                                <i className={`bi bi-chevron-${expandedRows[emp.emp_id] ? 'down' : 'right'}`}></i>
                                            </button>
                                            <span className="fw-semibold">{emp.emp_name}</span>
                                            <small className="text-muted ms-2">({emp.emp_code})</small>
                                        </div>
                                    </td>
                                    <td>{emp.dept_name}</td>
                                    <td>{emp.desig_name}</td>
                                    <td className="text-center fw-bold">{emp.ed}</td>
                                    <td className="text-center fw-bold text-success">{emp.wd}</td>
                                    <td className="text-center fw-bold text-info">{emp.fh}</td>
                                    <td className="text-center fw-bold text-warning">{emp.loff}</td>
                                    <td className="text-center fw-bold text-primary">{emp.pl || 0}</td>
                                    <td className="text-center fw-bold text-danger">{emp.lop}</td>

                                    {dateRange.map(date => {
                                        const dayData = emp.daily_data[date] || {};
                                        return (
                                            <td key={`${emp.emp_id}-${date}`} className="text-center p-1">
                                                <div className="d-flex justify-content-center">
                                                    {getStatusBadge(dayData)}
                                                </div>
                                            </td>
                                        );
                                    })}
                                </tr>

                                {/* Expanded Row for Details */}
                                {expandedRows[emp.emp_id] && (
                                    <tr className="bg-light">
                                        <td colSpan={12 + dateRange.length} className="p-3">
                                            <div className="row">
                                                <div className="col-md-4">
                                                    <h6 className="border-bottom pb-2">Employee Information</h6>
                                                    <table className="table table-sm table-borderless">
                                                        <tbody>
                                                            <tr>
                                                                <th>Code:</th>
                                                                <td>{emp.emp_code}</td>
                                                            </tr>
                                                            <tr>
                                                                <th>Name:</th>
                                                                <td>{emp.emp_name}</td>
                                                            </tr>
                                                            <tr>
                                                                <th>Department:</th>
                                                                <td>{emp.dept_name}</td>
                                                            </tr>
                                                            <tr>
                                                                <th>Designation:</th>
                                                                <td>{emp.desig_name}</td>
                                                            </tr>
                                                            <tr>
                                                                <th>Weekly Off:</th>
                                                                <td>
                                                                    {emp.weeklyoff_1}
                                                                    {emp.weeklyoff_2 ? `, ${emp.weeklyoff_2}` : ''}
                                                                </td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                                <div className="col-md-8">
                                                    <h6 className="border-bottom pb-2">Attendance Details</h6>
                                                    <div className="row">
                                                        {dateRange.map(date => {
                                                            const dayData = emp.daily_data[date] || {};
                                                            return (
                                                                <div key={`detail-${date}`} className="col-3 col-md-2 mb-2">
                                                                    <div className={`card border ${dayData.is_holiday ? 'border-purple' :
                                                                        dayData.is_layoff ? 'border-dark' :
                                                                            dayData.is_weeklyoff ? 'border-secondary' :
                                                                                'border-light'} p-1`}>
                                                                        <div className="text-center">
                                                                            <small className="d-block fw-semibold">
                                                                                {new Date(date).getDate()}
                                                                            </small>
                                                                            <small className="d-block text-muted">
                                                                                {new Date(date).toLocaleDateString('en-US', { weekday: 'short' })}
                                                                            </small>
                                                                        </div>
                                                                        <div className="text-center mt-1">
                                                                            {getStatusBadge(dayData)}
                                                                        </div>
                                                                        {dayData.in_time && (
                                                                            <small className="d-block text-center">
                                                                                {dayData.in_time} - {dayData.out_time || ''}
                                                                            </small>
                                                                        )}
                                                                        {dayData.work_hours > 0 && (
                                                                            <small className="d-block text-center">
                                                                                {dayData.work_hours}h
                                                                            </small>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}

                        {/* Summary Row */}
                        {attendanceData.length > 0 && (
                            <tr className="table-dark fw-bold">
                                <td colSpan="5" className="text-end">GRAND TOTAL:</td>
                                <td className="text-center">{summary.total_ed || 0}</td>
                                <td className="text-center">{summary.total_wd || 0}</td>
                                <td className="text-center">{summary.total_fh || 0}</td>
                                <td className="text-center">{summary.total_loff || 0}</td>
                                <td className="text-center">{summary.total_pl || 0}</td>
                                <td className="text-center">{summary.total_lop || 0}</td>
                                {dateRange.map(date => {
                                    const dailyTotal = summary.daily_totals?.find(d => d.date === date);
                                    return (
                                        <td key={`total-${date}`} className="text-center">
                                            <small className="d-block">{dailyTotal?.present_count || 0}</small>
                                            <small className="text-muted">P</small>
                                        </td>
                                    );
                                })}
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        );
    };

    // Render Card View
    const renderCardView = () => {
        return (
            <div className="row">
                {attendanceData.map((emp) => (
                    <div key={emp.emp_id} className="col-md-6 col-lg-4 mb-3">
                        <div className="card border-0 shadow-sm h-100">
                            <div className="card-header bg-light d-flex justify-content-between align-items-center">
                                <div>
                                    <h6 className="mb-0 fw-bold">{emp.emp_name}</h6>
                                    <small className="text-muted">{emp.emp_code} | {emp.dept_name}</small>
                                </div>
                                <span className="badge bg-primary">{emp.desig_name}</span>
                            </div>
                            <div className="card-body">
                                <div className="row mb-2">
                                    <div className="col-6">
                                        <small className="text-muted">Unit:</small>
                                        <div className="fw-semibold">{emp.unit_name}</div>
                                    </div>
                                    <div className="col-6">
                                        <small className="text-muted">Weekly Off:</small>
                                        <div className="fw-semibold">
                                            {emp.weeklyoff_1}
                                            {emp.weeklyoff_2 ? `, ${emp.weeklyoff_2}` : ''}
                                        </div>
                                    </div>
                                </div>

                                <div className="row text-center mb-3">
                                    <div className="col">
                                        <div className="border rounded p-2">
                                            <small className="text-muted d-block">ED</small>
                                            <div className="fw-bold fs-5">{emp.ed}</div>
                                        </div>
                                    </div>
                                    <div className="col">
                                        <div className="border rounded p-2 bg-success bg-opacity-10">
                                            <small className="text-muted d-block">WD</small>
                                            <div className="fw-bold fs-5 text-success">{emp.wd}</div>
                                        </div>
                                    </div>
                                    <div className="col">
                                        <div className="border rounded p-2 bg-primary bg-opacity-10">
                                            <small className="text-muted d-block">PL</small>
                                            <div className="fw-bold fs-5 text-primary">{emp.pl || 0}</div>
                                        </div>
                                    </div>
                                    <div className="col">
                                        <div className="border rounded p-2 bg-danger bg-opacity-10">
                                            <small className="text-muted d-block">LOP</small>
                                            <div className="fw-bold fs-5 text-danger">{emp.lop}</div>
                                        </div>
                                    </div>
                                </div>

                                <div className="attendance-calendar">
                                    <div className="row row-cols-7 g-1 mb-2">
                                        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, idx) => (
                                            <div key={idx} className="col text-center">
                                                <small className="text-muted">{day}</small>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="row row-cols-7 g-1">
                                        {dateRange.map(date => {
                                            const dayData = emp.daily_data[date] || {};
                                            const dayOfMonth = new Date(date).getDate();
                                            // eslint-disable-next-line no-unused-vars
                                            const dayOfWeek = new Date(date).getDay();

                                            return (
                                                <div key={date} className="col text-center">
                                                    <div
                                                        className={`day-cell ${selectedDate === date ? 'selected' : ''}`}
                                                        onClick={() => handleDateClick(date)}
                                                        title={`${date}: ${dayData.status_name || 'No record'}`}
                                                    >
                                                        <small className="d-block">{dayOfMonth}</small>
                                                        <div className="status-indicator">
                                                            {getStatusBadge(dayData)}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                            <div className="card-footer bg-transparent border-top">
                                <button
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => toggleRowExpand(emp.emp_id)}
                                >
                                    <i className="bi bi-eye me-1"></i>
                                    View Details
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    // Legend Component
    const renderLegend = () => {
        return (
            <div className="card border-0 mb-3">
                <div className="card-body py-2">
                    <div className="d-flex flex-wrap gap-3 align-items-center">
                        <small className="text-muted me-2">Legend:</small>
                        <div className="d-flex align-items-center">
                            <span className="badge bg-success me-1">P</span>
                            <small>Present</small>
                        </div>
                        <div className="d-flex align-items-center">
                            <span className="badge bg-danger me-1">A</span>
                            <small>Absent</small>
                        </div>
                        <div className="d-flex align-items-center">
                            <span className="badge bg-info me-1">PL</span>
                            <small>Paid Leave</small>
                        </div>
                        <div className="d-flex align-items-center">
                            <span className="badge bg-warning me-1">PA</span>
                            <small>Half Day</small>
                        </div>
                        <div className="d-flex align-items-center">
                            <span className="badge bg-purple me-1">H</span>
                            <small>Holiday</small>
                        </div>
                        <div className="d-flex align-items-center">
                            <span className="badge bg-purple me-1">HP</span>
                            <small>Holiday Present</small>
                        </div>
                        <div className="d-flex align-items-center">
                            <span className="badge bg-indigo me-1">WP</span>
                            <small>Weekly Off Present</small>
                        </div>
                        <div className="d-flex align-items-center">
                            <span className="badge bg-dark me-1">LO</span>
                            <small>Layoff</small>
                        </div>
                        <div className="d-flex align-items-center">
                            <span className="badge bg-secondary me-1">WO</span>
                            <small>Weekly Off</small>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="container-fluid py-3 attendance-view-container">
            {/* Header */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-body">
                            <div className="row align-items-center">
                                <div className="col-md-6">
                                    <h2 className="h4 mb-1 fw-bold text-dark">
                                        <i className="bi bi-calendar-check me-2 text-primary"></i>
                                        Attendance View
                                    </h2>
                                    <p className="text-muted mb-0">
                                        View attendance records with cross-tab analysis
                                    </p>
                                </div>
                                <div className="col-md-6 text-end">
                                    <button
                                        className="btn btn-outline-secondary"
                                        onClick={() => window.history.back()}
                                    >
                                        <i className="bi bi-arrow-left me-2"></i>
                                        Back
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-header bg-white border-bottom">
                            <h5 className="mb-0">
                                <i className="bi bi-funnel me-2 text-primary"></i>
                                Filters & Options
                            </h5>
                        </div>
                        <div className="card-body">
                            <div className="row g-3">
                                <div className="col-md-3">
                                    <label className="form-label">From Date</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={filters.from_date}
                                        onChange={(e) => handleFilterChange('from_date', e.target.value)}
                                    />
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label">To Date</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={filters.to_date}
                                        onChange={(e) => handleFilterChange('to_date', e.target.value)}
                                    />
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label">Unit</label>
                                    <select
                                        className="form-select"
                                        value={filters.unit_id}
                                        onChange={(e) => handleFilterChange('unit_id', e.target.value)}
                                    >
                                        <option value="">All Units</option>
                                        {units.map(unit => (
                                            <option key={unit.unit_id} value={unit.unit_id}>
                                                {unit.unit_name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label">Department</label>
                                    <select
                                        className="form-select"
                                        value={filters.department_id}
                                        onChange={(e) => handleFilterChange('department_id', e.target.value)}
                                    >
                                        <option value="">All Departments</option>
                                        {departments.map(dept => (
                                            <option key={dept.dept_id} value={dept.dept_id}>
                                                {dept.dept_name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label">View Type</label>
                                    <select
                                        className="form-select"
                                        value={filters.view_type}
                                        onChange={(e) => handleFilterChange('view_type', e.target.value)}
                                    >
                                        <option value="list">List View</option>
                                        <option value="card">Card View</option>
                                    </select>
                                </div>
                            </div>

                            <div className="row mt-3">
                                <div className="col-12">
                                    <div className="d-flex flex-wrap gap-2">
                                        <button
                                            className="btn btn-primary"
                                            onClick={fetchAttendanceData}
                                            disabled={loading}
                                        >
                                            {loading ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2"></span>
                                                    Loading...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="bi bi-search me-2"></i>
                                                    Load Attendance
                                                </>
                                            )}
                                        </button>

                                        <button
                                            className="btn btn-success"
                                            onClick={exportToExcel}
                                            disabled={loading || attendanceData.length === 0}
                                        >
                                            <i className="bi bi-file-excel me-2"></i>
                                            Export Excel
                                        </button>

                                        <button
                                            className="btn btn-danger"
                                            onClick={exportToPDF}
                                            disabled={loading || attendanceData.length === 0}
                                        >
                                            <i className="bi bi-file-pdf me-2"></i>
                                            Export PDF
                                        </button>

                                        <button
                                            className="btn btn-warning"
                                            onClick={printReport}
                                            disabled={loading || attendanceData.length === 0}
                                        >
                                            <i className="bi bi-printer me-2"></i>
                                            Print
                                        </button>

                                        <button
                                            className="btn btn-outline-secondary"
                                            onClick={() => {
                                                setFilters({
                                                    from_date: new Date().toISOString().split('T')[0],
                                                    to_date: new Date().toISOString().split('T')[0],
                                                    unit_id: '',
                                                    department_id: '',
                                                    view_type: 'list'
                                                });
                                                setAttendanceData([]);
                                            }}
                                        >
                                            <i className="bi bi-arrow-clockwise me-2"></i>
                                            Reset
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Summary Stats */}
            {attendanceData.length > 0 && (
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="card border-0 shadow-sm">
                            <div className="card-body py-3">
                                <div className="row text-center">
                                    <div className="col-md-2 mb-3 mb-md-0">
                                        <div className="fw-bold text-primary fs-4">{summary.total_employees}</div>
                                        <small className="text-muted">Total Employees</small>
                                    </div>
                                    <div className="col-md-2 mb-3 mb-md-0">
                                        <div className="fw-bold text-dark fs-4">{summary.total_ed}</div>
                                        <small className="text-muted">Total ED</small>
                                    </div>
                                    <div className="col-md-2 mb-3 mb-md-0">
                                        <div className="fw-bold text-success fs-4">{summary.total_wd}</div>
                                        <small className="text-muted">Total WD</small>
                                    </div>
                                    <div className="col-md-2 mb-3 mb-md-0">
                                        <div className="fw-bold text-info fs-4">{summary.total_fh}</div>
                                        <small className="text-muted">Total FH</small>
                                    </div>
                                    <div className="col-md-2 mb-3 mb-md-0">
                                        <div className="fw-bold text-warning fs-4">{summary.total_loff}</div>
                                        <small className="text-muted">Total LOff</small>
                                    </div>
                                    <div className="col-md-2 mb-3 mb-md-0">
                                        <div className="fw-bold text-primary fs-4">{summary.total_pl || 0}</div>
                                        <small className="text-muted">Total PL</small>
                                    </div>
                                    <div className="col-md-2 mb-3 mb-md-0">
                                        <div className="fw-bold text-danger fs-4">{summary.total_lop}</div>
                                        <small className="text-muted">Total LOP</small>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Legend */}
            {attendanceData.length > 0 && renderLegend()}

            {/* Data Display */}
            <div className="row">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-header bg-white border-bottom d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">
                                <i className="bi bi-table me-2 text-primary"></i>
                                Attendance Records
                                {attendanceData.length > 0 && (
                                    <span className="badge bg-primary ms-2">
                                        {attendanceData.length} Employees
                                    </span>
                                )}
                            </h5>
                            <div className="text-muted">
                                {filters.from_date} to {filters.to_date}
                            </div>
                        </div>
                        <div className="card-body p-0">
                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <p className="mt-3">Loading attendance data...</p>
                                </div>
                            ) : attendanceData.length === 0 ? (
                                <div className="text-center py-5">
                                    <i className="bi bi-calendar-x text-muted" style={{ fontSize: '3rem' }}></i>
                                    <h5 className="mt-3">No attendance records found</h5>
                                    <p className="text-muted">Select date range and click "Load Attendance"</p>
                                </div>
                            ) : filters.view_type === 'list' ? (
                                renderListView()
                            ) : (
                                renderCardView()
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Date Details Modal (for future implementation) */}
            {selectedDate && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    Attendance Details for {selectedDate}
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setSelectedDate(null)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                {/* Future implementation for detailed view */}
                                <p>Detailed view for {selectedDate} will be implemented here.</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                .attendance-view-container .sticky-header {
                    position: sticky;
                    top: 0;
                    z-index: 10;
                    background-color: #f8f9fa;
                }
                
                .attendance-view-container .attendance-table {
                    max-height: 70vh;
                    overflow-x: auto;
                    overflow-y: auto;
                }
                
                .attendance-view-container .day-cell {
                    border: 1px solid #dee2e6;
                    border-radius: 4px;
                    padding: 2px;
                    cursor: pointer;
                    transition: all 0.2s;
                }
                
                .attendance-view-container .day-cell:hover {
                    background-color: #f8f9fa;
                    transform: scale(1.05);
                }
                
                .attendance-view-container .day-cell.selected {
                    background-color: #e7f1ff;
                    border-color: #0d6efd;
                }
                
                .attendance-view-container .status-indicator {
                    min-height: 20px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                }
                
                .attendance-view-container .table th {
                    white-space: nowrap;
                    min-width: 50px;
                }
                
                .attendance-view-container .table td {
                    vertical-align: middle;
                }
                
                .attendance-calendar {
                    font-size: 0.8rem;
                }
                
                /* Custom colors for badges */
                .bg-purple {
                    background-color: #6f42c1 !important;
                    color: white !important;
                }
                
                .bg-indigo {
                    background-color: #6610f2 !important;
                    color: white !important;
                }
                
                @media print {
                    .attendance-view-container .card-header,
                    .attendance-view-container .btn {
                        display: none !important;
                    }
                    
                    .attendance-view-container .attendance-table {
                        max-height: none;
                        overflow: visible;
                    }
                }
            `}</style>
        </div>
    );
};

export default AttendanceView;